import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  axApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  AssetFilters,
  AssetListResponse,
  ComplianceHistoryFilters,
  DepreciationScheduleResponse,
  FacilityAlertsResponse,
  FacilityComplianceHistoryResponse,
  FacilityManagerResponse,
  FacilityPerformanceResponse,
  FacilityStatusResponse,
  FacilitySummaryResponse,
  FacilityTypeResponse,
  NetworkFilters,
  NetworksListResponse,
  OperationalHoursResponse,
  ParameterMonitoringResponse,
  ServiceOrderFilters,
  ServiceOrdersListResponse,
  SystemFilters,
  SystemsListResponse,
  TerritoryResponse,
  UtilityServiceOption,
} from "./types";

export const axAssetRegistry = {
  getServiceOrders: async (
    remoteUtilityId: string,
    filters: ServiceOrderFilters = {}
  ): Promise<ServiceOrdersListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);

        if (filters.requestId) {
          query.push("request_id", filters.requestId);
        }
        if (filters.search) {
          query.push("search_data", filters.search);
        }
        if (filters.page) {
          query.push("page", filters.page);
        }
        query.push("limit", PAGE_SIZE);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getFacilityFinance: async (
    facilityId: number,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `facilities/finance/${facilityId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getFacilitySystem: async (facilityId: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `facilities/finance/${facilityId}`
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getFacilitiesSummary: async (
    remoteUtilityId: string
  ): Promise<FacilitySummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "facilities-summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },
  getFacilities: async (filters?: Record<string, any>) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "facilities",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());

        if (filters) {
          if (filters.search) {
            query.push("search", filters.search);
          }

          if (filters.status !== undefined && filters.status !== null) {
            query.push("status", filters.status);
          }

          // Handle facilityType
          if (filters.facilityType) {
            query.push("facility_type", filters.facilityType);
          }

          // Handle serviceZone
          if (filters.serviceZone) {
            query.push("service_zone", filters.serviceZone);
          }

          // Handle criticalityLevel
          if (filters.criticalityLevel) {
            query.push("criticality_level", filters.criticalityLevel);
          }

          // Handle location
          if (filters.location) {
            query.push("location", filters.location);
          }

          // Handle date range
          if (filters.dateFrom) {
            query.push("date_from", filters.dateFrom);
          }

          if (filters.dateTo) {
            query.push("date_to", filters.dateTo);
          }

          // Handle pagination
          if (filters.page) {
            query.push("page", filters.page);
          }

          if (filters.limit) {
            query.push("limit", filters.limit);
          }

          // Handle sorting (if needed in future)
          if (filters.sort) {
            query.push("sort", filters.sort);
          }
        }

        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  toggleFacilityStatus: async (facilityId: number, isActive: boolean) => {
    const url = ApiEndpoints.createUrl("ax", `facilities/${facilityId}`);
    const response = await axApiClient.patch(url, {
      is_active: isActive,
    });
    return response.data;
  },

  getFacilityDetails: async (
    facilityId: string,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `facilities/${facilityId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.get(url);
    return response.data.result;
  },

  getFacilityPerformance: async (
    facilityId: number,
    remoteUtilityId: string
  ): Promise<FacilityPerformanceResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `facilities/performance/${facilityId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("date_filter", "monthly");
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  createFacility: async (formData: FormData): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", "facilities");

    const response = await axApiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  updateFacility: async (
    facilityId: string,
    formData: FormData
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", `facilities/${facilityId}`);

    const response = await axApiClient.patch(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  getFacilityStatuses: async (): Promise<FacilityStatusResponse> => {
    const url = ApiEndpoints.createUrl("ax", "choices/facility_status");
    const response = await axApiClient.get(url);
    return response.data;
  },

  getOperationalHours: async (): Promise<OperationalHoursResponse> => {
    const url = ApiEndpoints.createUrl("ax", "choices/operational_hours");
    const response = await axApiClient.get(url);
    return response.data;
  },
  getDepreciationSchedule: async (): Promise<DepreciationScheduleResponse> => {
    const url = ApiEndpoints.createUrl("ax", "choices/depreciation_schedule");
    const response = await axApiClient.get(url);
    return response.data;
  },

  getFacilityTypes: async (
    remoteUtilityId: string
  ): Promise<FacilityTypeResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/facility_type",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "facility_type");
        query.push("show_inactive", "true");
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getFacilityUtilityServices: async (
    remoteUtilityId: string
  ): Promise<UtilityServiceOption[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "utility/services",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );
    const response = await onboardingApiClient.get(url);

    const data = response.data?.result;
    return data
      .filter((item: any) => item.isActive)
      .map((item: any) => ({
        id: item.name,
        name: item.name,
        code: item.code || item.name,
        isActive: item.isActive,
      }));
  },

  getFacilityAreas: async (
    remoteUtilityId: string
  ): Promise<TerritoryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "area");
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getFacilityZones: async (
    remoteUtilityId: string
  ): Promise<TerritoryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "zone");
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getFacilityManagers: async (
    remoteUtilityId: string
  ): Promise<FacilityManagerResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "user-info",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", "1");
        query.push("limit", "50");
        query.push("role", "Plant Supervisor");
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  deleteFacility: async (
    facilityId: string,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `facilities/${facilityId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.delete(url);
    return response.data;
  },

  getSystems: async (
    remoteUtilityId: string,
    filters: SystemFilters = {}
  ): Promise<SystemsListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "systems",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", 1);

        if (filters.facilityId) {
          query.push("facility", filters.facilityId);
        }

        query.push("page", filters.page || 1);
        query.push("limit", filters.limit || 25);

        if (filters.search) {
          query.push("search", filters.search);
        }
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getFacilityAlerts: async (
    facilityId: string,
    remoteUtilityId: string
  ): Promise<FacilityAlertsResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `facilities/${facilityId}/alerts`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },
  getFacilityCompliance: async (
    facilityId: string,
    remoteUtilityId: string
  ): Promise<ParameterMonitoringResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `compliance/parameter-monitoring`,
      (query) => {
        query.push("entity_type", "0"); // 0 for facility
        query.push("entity_id", facilityId);
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getFacilityComplianceHistory: async (
    facilityId: string,
    remoteUtilityId: string,
    filters: ComplianceHistoryFilters = {}
  ): Promise<FacilityComplianceHistoryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `compliance/history`,
      (query) => {
        query.push("entity_type", "0"); // 0 for facility
        query.push("entity_id", facilityId);
        // Note: remote_utility_id might not be needed based on your example

        query.push("page", filters.page || 1);
        query.push("limit", filters.limit || 25);

        if (filters.search) {
          query.push("search", filters.search);
        }
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getNetworks: async (
    remoteUtilityId: string,
    filters: NetworkFilters = {}
  ): Promise<NetworksListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", 1);

        if (filters.facilityId) {
          query.push("facility_id", filters.facilityId);
        }

        query.push("page", filters.page || 1);
        query.push("limit", filters.limit || 25);

        if (filters.search) {
          query.push("search", filters.search);
        }

        if (filters.networkType?.length) {
          filters.networkType.forEach((type) =>
            query.push("network_type", type)
          );
        }

        if (filters.operationalStatus?.length) {
          filters.operationalStatus.forEach((status) =>
            query.push("operational_status", status)
          );
        }
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },
  getSystemConditionAndRiskById: async (
    assessmentName: string,
    id: number,
    remoteUtilityId: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `${assessmentName}/overview/${id}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
};
export const axAssets = {
  getAssets: async (
    remoteUtilityId: string,
    filters: AssetFilters = {}
  ): Promise<AssetListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "assets",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", 1);

        if (filters.facilityId) {
          query.push("facility", filters.facilityId);
        }

        query.push("page", filters.page);
        query.push("limit", filters.limit);

        if (filters.searchData) {
          query.push("search", filters.searchData);
        }

        if (filters.systemNetwork) {
          query.push("system_network", filters.systemNetwork);
        }

        if (filters.location) {
          query.push("location", filters.location);
        }

        if (filters.conditionScore) {
          query.push("condition_score", filters.conditionScore);
        }

        if (filters.riskScore) {
          query.push("risk_score", filters.riskScore);
        }

        if (filters.uptime) {
          query.push("uptime", filters.uptime);
        }
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },
};
